import { useState } from 'react';
import {
  Heart, Loader2, AlertCircle, Download, ChevronDown, ChevronUp,
  BookOpen, Lightbulb, Users, MessageCircle, Sparkles, Star,
  Trash2, Pencil, Check, X, Share2
} from 'lucide-react';
import { useSermons } from '../../hooks/useSermons';
import { useFamilyDevotionals } from '../../hooks/useFamilyDevotionals';
import { callAIFunction } from '../../lib/openai';
import { downloadDevotionalHTML, type DevotionalData } from '../../lib/devotionalTemplate';
import { logger } from '../../lib/logger';
import { supabase } from '../../lib/supabase';

type ExpandedSection = 'devotion' | 'memory' | 'object' | 'parental' | 'wonder' | 'application' | 'blessing';

const ALL_SECTIONS = new Set<ExpandedSection>([
  'devotion', 'memory', 'object', 'parental', 'wonder', 'application', 'blessing'
]);

// Inline editable text — textarea in edit mode, plain span otherwise
function ET({
  value, onChange, editing, rows,
}: {
  value: string; onChange: (v: string) => void; editing: boolean; rows?: number;
}) {
  if (!editing) return <>{value}</>;
  const r = rows ?? Math.max(2, Math.ceil(value.length / 72));
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={r}
      className="w-full resize-y rounded-md border border-electric/50 bg-white dark:bg-midnight px-2.5 py-1.5 text-sm text-midnight dark:text-soft-white focus:outline-none focus:ring-2 focus:ring-electric leading-relaxed"
    />
  );
}

export function FamilyDevotional({ defaultSermonId }: { defaultSermonId?: string } = {}) {
  const { sermons } = useSermons();
  const { devotionals, loading, error: loadError, save, update, remove } = useFamilyDevotionals();

  const [selectedSermonId, setSelectedSermonId] = useState(defaultSermonId ?? '');
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<ExpandedSection>>(new Set(ALL_SECTIONS));
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Edit mode
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DevotionalData | null>(null);
  const [saving, setSaving] = useState(false);

  // Share
  const [sharingId, setSharingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const generateShareId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 10; i++) result += chars.charAt(Math.floor(Math.random() * chars.length));
    return result;
  };

  const shareDevotional = async (dev: typeof devotionals[0]) => {
    setSharingId(dev.id);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let shareId = generateShareId();
      let attempts = 0;
      while (attempts < 3) {
        const { error: insertError } = await supabase
          .from('shared_devotional_pages')
          .insert([{
            share_id: shareId,
            user_id: user.id,
            sermon_title: dev.sermon_title,
            scripture: dev.scripture || null,
            date: dev.date || null,
            devotional_data: dev.devotional_data,
          }]);

        if (!insertError) {
          const url = `${window.location.origin}/devotional/${shareId}`;
          try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
          window.open(url, '_blank');
          setCopiedId(dev.id);
          setTimeout(() => setCopiedId(null), 3000);
          return;
        }
        if (insertError.code === '23505') {
          shareId = generateShareId();
          attempts++;
        } else {
          throw insertError;
        }
      }
      throw new Error('Failed to generate unique share ID');
    } catch (err) {
      logger.error('Share devotional error:', err);
      alert('Failed to create shareable link. Please try again.');
    } finally {
      setSharingId(null);
    }
  };

  const toggleSection = (section: ExpandedSection) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      next.has(section) ? next.delete(section) : next.add(section);
      return next;
    });
  };

  const startEdit = (id: string, data: DevotionalData) => {
    setDraft(JSON.parse(JSON.stringify(data)));
    setEditingId(id);
  };

  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const saveEdit = async (id: string) => {
    if (!draft) return;
    setSaving(true);
    try {
      await update(id, draft);
      setEditingId(null);
      setDraft(null);
    } catch (err) {
      logger.error('Save edit error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Helpers to update nested draft fields cleanly
  const setTop = (key: keyof DevotionalData, value: string) =>
    setDraft(p => p ? { ...p, [key]: value } : p);

  const setMV = (key: keyof DevotionalData['memoryVerse'], value: string) =>
    setDraft(p => p ? { ...p, memoryVerse: { ...p.memoryVerse, [key]: value } } : p);

  const setOL = (key: keyof DevotionalData['objectLesson'], value: string) =>
    setDraft(p => p ? { ...p, objectLesson: { ...p.objectLesson, [key]: value } } : p);

  const setPG = (key: keyof DevotionalData['parentalGuide'], value: string) =>
    setDraft(p => p ? { ...p, parentalGuide: { ...p.parentalGuide, [key]: value } } : p);

  const setWQ = (i: number, value: string) =>
    setDraft(p => {
      if (!p) return p;
      const wq = p.wonderQuestions.map((q, idx) => idx === i ? { ...q, question: value } : q);
      return { ...p, wonderQuestions: wq };
    });

  const setFA = (key: keyof DevotionalData['familyApplication'], value: string) =>
    setDraft(p => p ? { ...p, familyApplication: { ...p.familyApplication, [key]: value } } : p);

  const setCS = (i: number, value: string) =>
    setDraft(p => {
      if (!p) return p;
      const cs = p.familyApplication.conversationStarters.map((s, idx) => idx === i ? value : s);
      return { ...p, familyApplication: { ...p.familyApplication, conversationStarters: cs } };
    });

  const handleGenerate = async () => {
    if (!selectedSermonId) return;
    const sermon = sermons.find(s => s.id === selectedSermonId);
    if (!sermon) return;
    setGenerating(true);
    setGenError(null);
    try {
      const result = await callAIFunction('devotional', sermon.content, {
        title: sermon.title,
        scripture: sermon.scripture || '',
        date: sermon.date || '',
      });
      if (!result.success) throw new Error(result.error || 'Generation failed');
      const raw = typeof result.content === 'string' ? result.content : JSON.stringify(result.content);
      const parsed: DevotionalData = {
        sermonTitle: sermon.title,
        scripture: sermon.scripture || '',
        date: sermon.date || undefined,
        ...JSON.parse(raw),
      };
      const saved = await save(sermon.id, sermon.title, sermon.scripture || '', sermon.date || undefined, parsed);
      setExpandedId(saved.id);
      setSelectedSermonId('');
    } catch (err) {
      logger.error('Family devotional generation error:', err);
      setGenError(err instanceof Error ? err.message : 'Failed to generate devotional');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this devotional guide?')) return;
    setDeletingId(id);
    try {
      await remove(id);
      if (expandedId === id) setExpandedId(null);
      if (editingId === id) cancelEdit();
    } catch (err) {
      logger.error('Delete devotional error:', err);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-midnight dark:text-soft-white">Family Devotional Guide</h2>
      </div>

      {/* Generate panel */}
      <div className="bg-white dark:bg-charcoal rounded-xl shadow-sm border border-sand/20 dark:border-slate/20 p-6">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-10 h-10 rounded-lg bg-electric/10 flex items-center justify-center shrink-0">
            <Heart className="h-5 w-5 text-electric" />
          </div>
          <div>
            <h3 className="font-semibold text-midnight dark:text-soft-white">Generate New Devotional</h3>
            <p className="text-sm text-slate dark:text-soft-white/60 mt-0.5">
              Creates a full family devotional — main devotion, memory verse, object lesson, age guide, wonder questions, family application, and parental blessing.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-soft-white/70 mb-1.5">Select Sermon</label>
            <select
              value={selectedSermonId}
              onChange={e => setSelectedSermonId(e.target.value)}
              className="w-full px-3 py-2 border border-sand dark:border-slate/40 rounded-lg bg-white dark:bg-charcoal text-midnight dark:text-soft-white focus:ring-2 focus:ring-electric focus:border-transparent"
            >
              <option value="">Choose a sermon...</option>
              {sermons.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          </div>
          <button
            onClick={handleGenerate}
            disabled={!selectedSermonId || generating}
            className="flex items-center gap-2 px-5 py-2.5 bg-electric text-white font-medium rounded-lg hover:bg-electric/90 disabled:opacity-50 transition-colors"
          >
            {generating
              ? <><Loader2 className="h-4 w-4 animate-spin" />Generating Devotional…</>
              : <><Heart className="h-4 w-4" />Generate Devotional</>}
          </button>
        </div>
      </div>

      {genError && (
        <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 shrink-0" />{genError}
        </div>
      )}
      {loadError && (
        <div className="bg-crimson/10 text-crimson p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-5 w-5 shrink-0" />{loadError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-7 w-7 animate-spin text-electric" />
        </div>
      ) : devotionals.length === 0 && !generating ? (
        <div className="text-center py-14 bg-white dark:bg-charcoal rounded-xl border border-sand/20 dark:border-slate/20">
          <Heart className="h-12 w-12 text-slate/30 mx-auto mb-3" />
          <p className="text-slate dark:text-soft-white/50 text-sm">No devotionals yet — generate one from any of your sermons.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {devotionals.map(dev => {
            const isOpen = expandedId === dev.id;
            const isEditing = editingId === dev.id;
            // Use draft when editing, live data otherwise
            const d = (isEditing && draft) ? draft : dev.devotional_data;

            return (
              <div key={dev.id} className="bg-white dark:bg-charcoal rounded-xl shadow-sm border border-sand/20 dark:border-slate/20 overflow-hidden">

                {/* Card header */}
                <div
                  className="p-5 cursor-pointer hover:bg-sand/5 dark:hover:bg-slate/10 flex items-center justify-between"
                  onClick={() => !isEditing && setExpandedId(isOpen ? null : dev.id)}
                >
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-widest text-electric">Family Devotional</span>
                    <h3 className="font-semibold text-midnight dark:text-soft-white mt-0.5">
                      {dev.devotional_data.devotionalTitle || dev.sermon_title}
                    </h3>
                    <p className="text-xs text-slate dark:text-soft-white/50 mt-0.5">
                      {dev.sermon_title} · {new Date(dev.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    {!isEditing && (
                      <>
                        <button
                          onClick={() => { setExpandedId(dev.id); startEdit(dev.id, dev.devotional_data); }}
                          title="Edit devotional"
                          className="p-2 text-slate dark:text-soft-white/50 hover:text-electric rounded-lg transition-colors"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => shareDevotional(dev)}
                          disabled={sharingId === dev.id}
                          title={copiedId === dev.id ? 'Link copied & opened!' : 'Share public link'}
                          className="p-2 text-slate dark:text-soft-white/50 hover:text-emerald rounded-lg transition-colors disabled:opacity-50"
                        >
                          {sharingId === dev.id
                            ? <Loader2 className="h-5 w-5 animate-spin" />
                            : copiedId === dev.id
                              ? <Check className="h-5 w-5 text-emerald" />
                              : <Share2 className="h-5 w-5" />}
                        </button>
                        <button
                          onClick={() => downloadDevotionalHTML(dev.devotional_data)}
                          title="Download HTML"
                          className="p-2 text-slate dark:text-soft-white/50 hover:text-electric rounded-lg transition-colors"
                        >
                          <Download className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(dev.id)}
                          disabled={deletingId === dev.id}
                          title="Delete"
                          className="p-2 text-slate dark:text-soft-white/50 hover:text-crimson rounded-lg transition-colors disabled:opacity-40"
                        >
                          {deletingId === dev.id ? <Loader2 className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                        </button>
                      </>
                    )}
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => saveEdit(dev.id)}
                          disabled={saving}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-electric text-white text-xs font-semibold rounded-lg hover:bg-electric/90 disabled:opacity-50 transition-colors"
                        >
                          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-sand/20 dark:bg-slate/30 text-slate dark:text-soft-white/70 text-xs font-semibold rounded-lg hover:bg-sand/40 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                          Cancel
                        </button>
                      </>
                    ) : (
                      isOpen
                        ? <ChevronUp className="h-5 w-5 text-slate dark:text-soft-white/40 ml-1" />
                        : <ChevronDown className="h-5 w-5 text-slate dark:text-soft-white/40 ml-1" />
                    )}
                  </div>
                </div>

                {/* Expanded content */}
                {isOpen && (
                  <div className="border-t border-sand/20 dark:border-slate/20 divide-y divide-sand/10 dark:divide-slate/10">

                    {/* Theme + key verse */}
                    <div className="px-5 py-4 bg-electric/5 dark:bg-electric/10 space-y-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-electric mb-1">Devotional Title</p>
                        {isEditing
                          ? <ET value={draft!.devotionalTitle} onChange={v => setTop('devotionalTitle', v)} editing rows={1} />
                          : <p className="text-sm font-medium text-midnight dark:text-soft-white">{d.devotionalTitle}</p>}
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-electric mb-1">Theme</p>
                        {isEditing
                          ? <ET value={draft!.theme} onChange={v => setTop('theme', v)} editing rows={1} />
                          : <p className="text-sm font-medium text-midnight dark:text-soft-white">{d.theme}</p>}
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-electric mb-1">Key Verse</p>
                        {isEditing
                          ? <ET value={draft!.keyVerse} onChange={v => setTop('keyVerse', v)} editing rows={2} />
                          : <p className="text-sm italic text-slate dark:text-soft-white/60 border-l-2 border-electric/40 pl-3">{d.keyVerse}</p>}
                      </div>
                    </div>

                    {/* Main Devotion */}
                    <SectionRow icon={<BookOpen className="h-4 w-4" />} label="Main Devotion" section="devotion" expanded={expandedSections.has('devotion')} onToggle={() => toggleSection('devotion')}>
                      {isEditing
                        ? <ET value={draft!.mainDevotion} onChange={v => setTop('mainDevotion', v)} editing rows={12} />
                        : <div className="text-sm text-slate dark:text-soft-white/70 leading-relaxed space-y-3">
                            {d.mainDevotion.split(/\n\n+/).filter(Boolean).map((p, i) => <p key={i}>{p}</p>)}
                          </div>}
                    </SectionRow>

                    {/* Memory Verse */}
                    <SectionRow icon={<Star className="h-4 w-4" />} label="Memory Verse" section="memory" expanded={expandedSections.has('memory')} onToggle={() => toggleSection('memory')}>
                      <div className="space-y-3">
                        {isEditing ? (
                          <div className="space-y-2">
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Verse Text</label>
                            <ET value={draft!.memoryVerse.text} onChange={v => setMV('text', v)} editing rows={3} />
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Reference</label>
                            <ET value={draft!.memoryVerse.reference} onChange={v => setMV('reference', v)} editing rows={1} />
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Hand Motion Tip</label>
                            <ET value={draft!.memoryVerse.motionTip} onChange={v => setMV('motionTip', v)} editing rows={2} />
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Melody Hint</label>
                            <ET value={draft!.memoryVerse.melodyHint} onChange={v => setMV('melodyHint', v)} editing rows={1} />
                          </div>
                        ) : (
                          <>
                            <blockquote className="text-base italic text-midnight dark:text-soft-white font-medium border-l-4 border-electric pl-4">
                              "{d.memoryVerse.text}"
                              <cite className="block text-xs not-italic text-slate dark:text-soft-white/50 mt-1 font-semibold uppercase tracking-wider">{d.memoryVerse.reference}</cite>
                            </blockquote>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-sand/10 dark:bg-slate/20 rounded-lg p-3">
                                <p className="text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1">🤲 Hand Motions</p>
                                <p className="text-sm text-midnight dark:text-soft-white">{d.memoryVerse.motionTip}</p>
                              </div>
                              <div className="bg-sand/10 dark:bg-slate/20 rounded-lg p-3">
                                <p className="text-xs font-semibold text-slate dark:text-soft-white/50 uppercase tracking-wider mb-1">🎵 Melody</p>
                                <p className="text-sm text-midnight dark:text-soft-white">{d.memoryVerse.melodyHint}</p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </SectionRow>

                    {/* Object Lesson */}
                    <SectionRow icon={<Lightbulb className="h-4 w-4" />} label={`Object Lesson: ${d.objectLesson.item}`} section="object" expanded={expandedSections.has('object')} onToggle={() => toggleSection('object')}>
                      {isEditing ? (
                        <div className="space-y-2">
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Item</label>
                          <ET value={draft!.objectLesson.item} onChange={v => setOL('item', v)} editing rows={1} />
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">How To</label>
                          <ET value={draft!.objectLesson.howTo} onChange={v => setOL('howTo', v)} editing rows={3} />
                          <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Connection</label>
                          <ET value={draft!.objectLesson.connection} onChange={v => setOL('connection', v)} editing rows={2} />
                        </div>
                      ) : (
                        <div className="space-y-2 text-sm">
                          <p className="text-slate dark:text-soft-white/70">{d.objectLesson.howTo}</p>
                          <p className="italic text-electric font-medium">→ {d.objectLesson.connection}</p>
                        </div>
                      )}
                    </SectionRow>

                    {/* Parental Guide */}
                    <SectionRow icon={<Users className="h-4 w-4" />} label="Understanding Together (Age Guide)" section="parental" expanded={expandedSections.has('parental')} onToggle={() => toggleSection('parental')}>
                      {isEditing ? (
                        <div className="space-y-2">
                          {([['littleOnes', 'Ages 3–6'], ['children', 'Ages 7–11'], ['tweens', 'Ages 12+']] as const).map(([key, label]) => (
                            <div key={key}>
                              <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">{label}</label>
                              <ET value={draft!.parentalGuide[key]} onChange={v => setPG(key, v)} editing rows={3} />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          {([
                            { label: 'Ages 3–6', color: 'border-green-400', key: 'littleOnes' as const },
                            { label: 'Ages 7–11', color: 'border-blue-400', key: 'children' as const },
                            { label: 'Ages 12+', color: 'border-orange-400', key: 'tweens' as const },
                          ]).map(({ label, color, key }) => (
                            <div key={label} className={`rounded-lg p-3 bg-sand/10 dark:bg-slate/20 border-t-2 ${color}`}>
                              <p className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50 mb-1.5">{label}</p>
                              <p className="text-sm text-midnight dark:text-soft-white leading-relaxed">{d.parentalGuide[key]}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </SectionRow>

                    {/* Wonder Questions */}
                    <SectionRow icon={<Sparkles className="h-4 w-4" />} label="Wonder Together" section="wonder" expanded={expandedSections.has('wonder')} onToggle={() => toggleSection('wonder')}>
                      <div className="space-y-2">
                        {(d.wonderQuestions || []).map((q, i) => (
                          <div key={i} className="flex gap-3 items-start bg-sand/10 dark:bg-slate/20 rounded-lg p-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-electric shrink-0 w-14 pt-1">{q.age}</span>
                            {isEditing
                              ? <ET value={draft!.wonderQuestions[i].question} onChange={v => setWQ(i, v)} editing rows={2} />
                              : <p className="text-sm italic text-midnight dark:text-soft-white">{q.question}</p>}
                          </div>
                        ))}
                      </div>
                    </SectionRow>

                    {/* Family Application */}
                    <SectionRow icon={<MessageCircle className="h-4 w-4" />} label="Family Application" section="application" expanded={expandedSections.has('application')} onToggle={() => toggleSection('application')}>
                      <div className="space-y-3">
                        <div className="bg-sand/10 dark:bg-slate/20 rounded-lg p-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50 mb-1">🍽 Dinner Table Question</p>
                          {isEditing
                            ? <ET value={draft!.familyApplication.tableQuestion} onChange={v => setFA('tableQuestion', v)} editing rows={2} />
                            : <p className="text-sm italic text-midnight dark:text-soft-white">{d.familyApplication.tableQuestion}</p>}
                        </div>
                        <div className="bg-sand/10 dark:bg-slate/20 rounded-lg p-3">
                          <p className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50 mb-1">🎯 Weekly Challenge</p>
                          {isEditing
                            ? <ET value={draft!.familyApplication.weeklyChallenge} onChange={v => setFA('weeklyChallenge', v)} editing rows={2} />
                            : <p className="text-sm text-midnight dark:text-soft-white">{d.familyApplication.weeklyChallenge}</p>}
                        </div>
                        {d.familyApplication.conversationStarters?.length > 0 && (
                          <div className="bg-sand/10 dark:bg-slate/20 rounded-lg p-3">
                            <p className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50 mb-1.5">💬 Conversation Starters</p>
                            <div className="space-y-1.5">
                              {d.familyApplication.conversationStarters.map((s, i) => (
                                isEditing
                                  ? <ET key={i} value={draft!.familyApplication.conversationStarters[i]} onChange={v => setCS(i, v)} editing rows={2} />
                                  : <p key={i} className="text-sm text-midnight dark:text-soft-white flex gap-2"><span className="text-electric shrink-0">·</span>{s}</p>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="bg-electric/10 dark:bg-electric/15 rounded-lg p-3 border border-electric/20">
                          <p className="text-xs font-semibold uppercase tracking-wider text-electric mb-1.5">🙏 Family Prayer</p>
                          {isEditing
                            ? <ET value={draft!.familyApplication.prayerGuide} onChange={v => setFA('prayerGuide', v)} editing rows={4} />
                            : <p className="text-sm italic text-midnight dark:text-soft-white leading-relaxed">{d.familyApplication.prayerGuide}</p>}
                        </div>
                      </div>
                    </SectionRow>

                    {/* Parental Blessing */}
                    <SectionRow icon={<Heart className="h-4 w-4" />} label="Parental Blessing" section="blessing" expanded={expandedSections.has('blessing')} onToggle={() => toggleSection('blessing')}>
                      <div className={isEditing ? 'space-y-2' : 'text-center py-2'}>
                        {isEditing ? (
                          <>
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Blessing</label>
                            <ET value={draft!.parentalBlessing} onChange={v => setTop('parentalBlessing', v)} editing rows={3} />
                            <label className="text-xs font-semibold uppercase tracking-wider text-slate dark:text-soft-white/50">Closing Thought</label>
                            <ET value={draft!.closingThought} onChange={v => setTop('closingThought', v)} editing rows={2} />
                          </>
                        ) : (
                          <>
                            <p className="text-base italic text-midnight dark:text-soft-white font-medium leading-relaxed max-w-lg mx-auto">"{d.parentalBlessing}"</p>
                            {d.closingThought && (
                              <p className="text-sm text-slate dark:text-soft-white/50 mt-4 border-t border-sand/20 dark:border-slate/20 pt-4 italic">"{d.closingThought}"</p>
                            )}
                          </>
                        )}
                      </div>
                    </SectionRow>

                    {/* Footer */}
                    <div className="px-5 py-4 bg-sand/5 dark:bg-slate/10 flex items-center justify-between gap-4">
                      <p className="text-xs text-slate dark:text-soft-white/40">
                        {isEditing ? 'Save your changes before downloading.' : 'Download as a beautifully designed HTML page — print to PDF from your browser.'}
                      </p>
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <button onClick={cancelEdit} className="px-4 py-2 text-sm font-medium text-slate dark:text-soft-white/60 rounded-lg hover:bg-sand/20 transition-colors">
                            Cancel
                          </button>
                          <button
                            onClick={() => saveEdit(dev.id)}
                            disabled={saving}
                            className="flex items-center gap-2 px-4 py-2 bg-electric text-white text-sm font-medium rounded-lg hover:bg-electric/90 disabled:opacity-50 transition-colors"
                          >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                            Save Changes
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => shareDevotional(dev)}
                            disabled={sharingId === dev.id}
                            className="flex items-center gap-2 px-4 py-2 bg-emerald/90 text-white text-sm font-medium rounded-lg hover:bg-emerald disabled:opacity-50 transition-colors"
                          >
                            {sharingId === dev.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : copiedId === dev.id
                                ? <Check className="h-4 w-4" />
                                : <Share2 className="h-4 w-4" />}
                            {copiedId === dev.id ? 'Link Copied!' : 'Share Link'}
                          </button>
                          <button
                            onClick={() => downloadDevotionalHTML(d)}
                            className="flex items-center gap-2 px-4 py-2 bg-electric text-white text-sm font-medium rounded-lg hover:bg-electric/90 transition-colors"
                          >
                            <Download className="h-4 w-4" />
                            Download HTML
                          </button>
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface SectionRowProps {
  icon: React.ReactNode;
  label: string;
  section: ExpandedSection;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function SectionRow({ icon, label, expanded, onToggle, children }: SectionRowProps) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-sand/5 dark:hover:bg-slate/10 text-left transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <span className="text-electric">{icon}</span>
          <span className="text-sm font-semibold text-midnight dark:text-soft-white">{label}</span>
        </div>
        {expanded
          ? <ChevronUp className="h-4 w-4 text-slate dark:text-soft-white/40 shrink-0" />
          : <ChevronDown className="h-4 w-4 text-slate dark:text-soft-white/40 shrink-0" />}
      </button>
      {expanded && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
}
